<template>
  <div class="collect-payment-view">
    <!-- Back header -->
    <div class="view-header">
      <button class="back-btn" @click="$emit('back')">
        <i class="tim-icons icon-minimal-left"></i>
      </button>
      <h5 class="view-title mb-0">Collect payment</h5>
    </div>

    <div class="view-body">
      <!-- Full transaction summary mirrors the BookingDrawer's summary
           section so staff can verify what's being paid for and capture
           tip / promo without leaving the screen. The block emits `updated`
           on a successful tip/promo POST; the parent re-fetches and the
           `booking` watch below resets `paymentAmount` to the new due. -->
      <transaction-summary-block
        class="collect-payment-summary"
        :current-booking="{
          game_name: booking.game_name,
          group_size: booking.group_size,
          price: booking.price
        }"
        :sibling-bookings="siblingBookings"
        :line-items="lineItems"
        :gift-cards="giftCards"
        :financials="financials"
        :tax-lines="taxLines"
        :fee-lines="feeLines"
        :discount-lines="discountLines"
        :tip-lines="tipLines"
        :transaction-id="booking.transaction_id"
        :company-id="companyId"
        :company-group-id="companyGroupId"
        :currency="currency"
        :currency-symbol="currencySymbol"
        :currency-format="currencyFormat"
        :number-format="numberFormat"
        :can-update="canUpdate"
        @updated="$emit('updated')"
      />


      <!-- Amount -->
      <div class="row">
        <div class="col-auto">
          <label class="field-label">Amount</label>
          <div class="amount-input-group" :class="{ 'has-error': amountExceedsDue }">
            <span class="amount-prefix">{{ resolvedCurrencySymbol }}</span>
            <base-input class="amount-input-wrapper mb-0">
              <el-input-number
                :controls="false"
                v-model="paymentAmount"
                :min="0"
                :precision="2"
                placeholder="0.00"
              />
            </base-input>
          </div>
          <div v-if="amountExceedsDue || giftCardExceeded || customerCreditExceeded" class="form-group has-danger mb-0">
            <label v-if="amountExceedsDue" class="error">Amount exceeds balance due ({{ getAmount(booking.due) }})</label>
            <label v-else-if="giftCardExceeded" class="error">Amount exceeds gift card balance ({{ getAmount(selectedGiftCard.remaining) }})</label>
            <label v-else-if="customerCreditExceeded" class="error">Amount exceeds available credit ({{ getAmount(availableCredit) }})</label>
          </div>
        </div>
        <div class="col-auto" v-if="selectedMethod && selectedMethod.name === 'Customer credit'">
          <label class="field-label">Available credit</label>
          <p class="text-white mb-0">{{ getAmount(availableCredit) }}</p>
        </div>
        <div class="col-auto" v-if="selectedMethod && selectedMethod.name === 'Gift card' && selectedGiftCard">
          <label class="field-label">Available balance</label>
          <p class="text-white mb-0">{{ getAmount(selectedGiftCard.remaining) }}</p>
        </div>
      </div>

      <!-- Payment method -->
      <div class="row mt-3">
        <div class="col-6">
          <base-input label="Payment options">
            <el-select
              class="select-primary"
              v-model="selectedMethod"
              placeholder="Select method"
              value-key="id"
            >
              <el-option
                v-for="(method, index) in paymentOptions"
                class="select-primary"
                :label="method.name"
                :value="{id: method.id, name: method.name, content: method.content, is_default: method.is_default}"
                :key="'PM' + index"
              ></el-option>
            </el-select>
          </base-input>
        </div>
        <!-- Stored cards (Stripe) -->
        <div class="col-6" v-if="selectedMethod && selectedMethod.name === 'Card' && gatewayId === 3 && paymentSettings.store_credit_card && stripePaymentMethods.length > 0">
          <base-input label="Stored cards">
            <el-select
              class="select-primary"
              v-model="selectedPaymentMethod"
              placeholder="Select card"
            >
              <el-option class="select-primary" label="Use a new card" value="new"></el-option>
              <el-option
                class="select-primary"
                v-for="pm in stripePaymentMethods"
                :key="pm.id"
                :label="getCardBrandName(pm.card_brand) + ' ****' + pm.card_last_four + (pm.is_default ? ' (Default)' : '')"
                :value="pm.id"
              >
                <span>
                  <i :class="getCardBrandIcon(pm.card_brand)" class="mr-2"></i>
                  {{ getCardBrandName(pm.card_brand) }} ****{{ pm.card_last_four }}
                  <badge v-if="pm.is_default" type="success" class="ml-2">Default</badge>
                </span>
              </el-option>
            </el-select>
          </base-input>
        </div>
        <!-- Stored cards (Square) -->
        <div class="col-6" v-if="selectedMethod && selectedMethod.name === 'Card' && gatewayId === 2 && paymentSettings.square_store_credit_card && squarePaymentMethods.length > 0">
          <base-input label="Stored cards">
            <el-select
              class="select-primary"
              v-model="selectedPaymentMethod"
              placeholder="Select card"
            >
              <el-option class="select-primary" label="Use a new card" value="new"></el-option>
              <el-option
                class="select-primary"
                v-for="pm in squarePaymentMethods"
                :key="pm.id"
                :label="getCardBrandName(pm.card_brand) + ' ****' + pm.card_last_four + (pm.is_default ? ' (Default)' : '')"
                :value="pm.id"
              >
                <span>
                  <i :class="getCardBrandIcon(pm.card_brand)" class="mr-2"></i>
                  {{ getCardBrandName(pm.card_brand) }} ****{{ pm.card_last_four }}
                  <badge v-if="pm.is_default" type="success" class="ml-2">Default</badge>
                </span>
              </el-option>
            </el-select>
          </base-input>
        </div>
      </div>

      <!-- Custom payment option content -->
      <div class="row" v-if="selectedMethod && !selectedMethod.is_default">
        <div class="col">
          <div v-html="selectedMethod.content"></div>
        </div>
      </div>

      <!-- Gift card lookup -->
      <div class="row mb-3" v-if="selectedMethod && selectedMethod.name === 'Gift card'">
        <div class="col">
          <base-input label="Gift card code" :error="giftCardError">
            <el-autocomplete style="width: 100%"
              v-model="giftCardCode"
              :fetch-suggestions="giftCardSearch"
              placeholder="Search by code..."
              :trigger-on-focus="false"
              @select="handleGiftCardSelect"
            >
              <template #default="{ item }">
                <div class="row">
                  <div class="col">
                    <p style="margin-bottom: -12px" class="text-primary">{{ item.code }}</p>
                    <small class="font-italic">
                      <i class="tim-icons icon-money-coins"></i> Total {{ getAmount(item.total) }} Remaining {{ getAmount(item.remaining) }}
                    </small>
                    <br/>
                  </div>
                </div>
              </template>
            </el-autocomplete>
          </base-input>
        </div>
      </div>

      <!-- Cardholder / payee name -->
      <div class="row" v-if="showPayeeFields">
        <div class="col-6">
          <base-input :label="payeeFirstNameLabel" v-model="cardholderFirstName" placeholder="First name"></base-input>
        </div>
        <div class="col-6">
          <base-input :label="payeeLastNameLabel" v-model="cardholderLastName" placeholder="Last name"></base-input>
        </div>
      </div>

      <!-- Square card form -->
      <div v-show="selectedMethod && selectedMethod.name === 'Card' && gatewayId === 2">
        <div class="row mt-2" v-show="selectedPaymentMethod === 'new' || squarePaymentMethods.length === 0">
          <div class="col">
            <form id="payment-form-square-collect">
              <div id="sq-card-collect"></div>
            </form>
          </div>
        </div>

        <!-- Save card toggle (Square) -->
        <div class="row mt-4 mb-3" v-if="(selectedPaymentMethod === 'new' || squarePaymentMethods.length === 0) && paymentSettings.square_store_credit_card && paymentSettings.square_create_customer && booking.end_user_id">
          <div class="col">
            <el-switch active-color="#e14eca" v-model="saveCard" />
            <label class="text-white opacity-7 ml-2">Save this card for future payments</label>
          </div>
        </div>

        <!-- Selected stored card info (Square) -->
        <div class="row mb-3" v-if="selectedPaymentMethod !== 'new' && selectedSquareStoredCard">
          <div class="col">
            <div class="stored-card-display">
              <div class="d-flex align-items-center">
                <i :class="getCardBrandIcon(selectedSquareStoredCard.card_brand)" class="text-white mr-3" style="font-size: 2rem;"></i>
                <div>
                  <p class="text-white mb-0">{{ getCardBrandName(selectedSquareStoredCard.card_brand) }} ****{{ selectedSquareStoredCard.card_last_four }}</p>
                  <small class="text-white opacity-7" v-if="selectedSquareStoredCard.cardholder_name">{{ selectedSquareStoredCard.cardholder_name }}</small>
                  <small class="text-white opacity-7"> &middot; Expires {{ selectedSquareStoredCard.card_exp_month }}/{{ selectedSquareStoredCard.card_exp_year }}</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Stripe card form -->
      <div v-show="selectedMethod && selectedMethod.name === 'Card' && gatewayId === 3">
        <div class="row mb-3" v-show="selectedPaymentMethod === 'new'">
          <div class="col">
            <form id="payment-form-collect">
              <label for="card-element-collect">Credit or debit card</label>
              <div id="card-element-collect" class="stripe-card-element"></div>
              <div id="card-errors-collect" role="alert"></div>
            </form>
          </div>
        </div>

        <!-- Save card toggle (Stripe) -->
        <div class="row mb-3" v-if="selectedPaymentMethod === 'new' && paymentSettings.store_credit_card && booking.end_user_id">
          <div class="col">
            <el-switch active-color="#e14eca" v-model="saveCard" />
            <label class="text-white opacity-7 ml-2">Save this card for future purchases</label>
          </div>
        </div>

        <!-- Selected stored card info (Stripe) -->
        <div class="row mb-3" v-if="selectedPaymentMethod !== 'new' && selectedStripeStoredCard">
          <div class="col">
            <div class="stored-card-display">
              <div class="d-flex align-items-center">
                <i :class="getCardBrandIcon(selectedStripeStoredCard.card_brand)" class="text-white mr-3" style="font-size: 2rem;"></i>
                <div>
                  <p class="text-white mb-0">{{ getCardBrandName(selectedStripeStoredCard.card_brand) }} ****{{ selectedStripeStoredCard.card_last_four }}</p>
                  <small class="text-white opacity-7" v-if="selectedStripeStoredCard.cardholder_name">{{ selectedStripeStoredCard.cardholder_name }}</small>
                  <small class="text-white opacity-7"> &middot; Expires {{ selectedStripeStoredCard.card_exp_month }}/{{ selectedStripeStoredCard.card_exp_year }}</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Authorize.net card form -->
      <div class="row" v-if="selectedMethod && selectedMethod.name === 'Card' && gatewayId === 1">
        <div class="col-12">
          <enhanced-payment-form
            ref="authorizePaymentForm"
            :api-login-id="paymentSettings.api_name"
            :public-client-key="paymentSettings.public_client_key"
            :is-live="!!paymentSettings.is_live"
            :show-security-indicators="false"
          />
        </div>
      </div>

      <!-- Paystack info -->
      <div v-if="selectedMethod && selectedMethod.name === 'Card' && gatewayId === 4" class="paystack-info mb-3">
        <div class="d-flex align-items-center">
          <i class="tim-icons icon-lock-circle mr-2 text-success"></i>
          <span class="text-white small">You will be redirected to Paystack to complete your payment securely.</span>
        </div>
      </div>

      <!-- Error message -->
      <div v-if="errorMessage" class="payment-error mt-3">
        <i class="tim-icons icon-alert-circle-exc mr-1"></i>
        {{ errorMessage }}
      </div>

      <!-- Success message -->
      <div v-if="successMessage" class="payment-success mt-3">
        <i class="tim-icons icon-check-2 mr-1"></i>
        {{ successMessage }}
      </div>
    </div>

    <!-- Footer -->
    <div class="view-footer">
      <base-button size="sm" type="default" @click="$emit('back')">Cancel</base-button>
      <base-button
        size="sm"
        type="primary"
        :loading="processing"
        :disabled="!canSubmit || !!successMessage"
        @click="submitPayment"
      >
        {{ processing ? 'Processing...' : 'Add payment' }}
      </base-button>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { API_LOCATION } from '@/api-config';
import { Auth } from 'aws-amplify';
import { Select, Option, Autocomplete, InputNumber, Switch } from 'element-ui';
import EnhancedPaymentForm from '@/components/Payments/EnhancedPaymentForm';
import currencies from '@/assets/currencies.json';
import { formatCurrency } from '@/util/currencyFormat';
import { allowsOverpayment } from '@/util/paymentAmountCap';
import { toCents } from '@/util/pricing';
import TransactionSummaryBlock from '../components/TransactionSummaryBlock.vue';

export default {
  name: 'CollectPaymentView',

  components: {
    [Select.name]: Select,
    [Option.name]: Option,
    [Autocomplete.name]: Autocomplete,
    [InputNumber.name]: InputNumber,
    [Switch.name]: Switch,
    EnhancedPaymentForm,
    TransactionSummaryBlock
  },

  props: {
    booking: { type: Object, required: true },
    companyId: { type: [String, Number], required: true },
    companyGroupId: { type: [String, Number], required: true },
    currencySymbol: { type: String, default: '$' },
    currency: { type: String, default: 'USD' },
    currencyFormat: { type: Number, default: 0 },
    numberFormat: { type: Number, default: 0 },
    paymentSettings: { type: Object, default: () => ({}) },
    // Transaction summary inputs — mirror what BookingDrawer feeds the
    // summary section so the same block renders here.
    financials: { type: Object, default: () => ({}) },
    siblingBookings: { type: Array, default: () => [] },
    lineItems: { type: Array, default: () => [] },
    giftCards: { type: Array, default: () => [] },
    taxLines: { type: Array, default: () => [] },
    feeLines: { type: Array, default: () => [] },
    discountLines: { type: Array, default: () => [] },
    tipLines: { type: Array, default: () => [] },
    canUpdate: { type: Boolean, default: false }
  },

  data() {
    return {
      paymentAmount: 0,
      selectedMethod: null,
      selectedPaymentMethod: 'new',
      paymentOptions: [],
      processing: false,
      errorMessage: '',
      successMessage: '',
      stripe: null,
      stripeCard: null,
      stripeCustomer: null,
      square: null,
      squareCard: null,
      squareCustomer: null,
      stripePaymentMethods: [],
      squarePaymentMethods: [],
      cardholderFirstName: '',
      cardholderLastName: '',
      saveCard: false,
      availableCredit: '0.00',
      giftCardCode: '',
      selectedGiftCard: null,
      giftCardError: '',
      authorize_opaque_data: null,
      authorize_zip: null
    };
  },

  computed: {
    resolvedCurrencySymbol() {
      const info = currencies.currencies.find(c => c.code === this.currency);
      return info ? info.symbol : this.currencySymbol;
    },
    gatewayId() {
      return this.paymentSettings.payment_gateway_id || null;
    },
    effectiveAmount() {
      if (!this.paymentAmount || this.paymentAmount <= 0) return parseFloat(this.booking.due);
      return this.paymentAmount;
    },
    giftCardExceeded() {
      return this.selectedMethod
        && this.selectedMethod.name === 'Gift card'
        && this.selectedGiftCard
        && toCents(this.effectiveAmount) > toCents(this.selectedGiftCard.remaining);
    },
    customerCreditExceeded() {
      return this.selectedMethod
        && this.selectedMethod.name === 'Customer credit'
        && toCents(this.effectiveAmount) > toCents(this.availableCredit);
    },
    // Cash and other record-only methods may exceed the balance; the negative
    // due is the change owed back. Card, gift card and credit keep the cap.
    canExceedDue() {
      return !!this.selectedMethod && allowsOverpayment(this.selectedMethod.name);
    },
    amountExceedsDue() {
      return !this.canExceedDue && toCents(this.effectiveAmount) > toCents(this.booking.due);
    },
    canSubmit() {
      if (!this.selectedMethod) return false;
      if (this.effectiveAmount <= 0) return false;
      if (this.amountExceedsDue) return false;
      if (this.selectedMethod.name === 'Gift card' && !this.selectedGiftCard) return false;
      if (this.giftCardExceeded) return false;
      if (this.customerCreditExceeded) return false;
      return true;
    },
    selectedStripeStoredCard() {
      if (this.selectedPaymentMethod === 'new') return null;
      return this.stripePaymentMethods.find(pm => pm.id === this.selectedPaymentMethod);
    },
    selectedSquareStoredCard() {
      if (this.selectedPaymentMethod === 'new') return null;
      return this.squarePaymentMethods.find(pm => pm.id === this.selectedPaymentMethod);
    },
    showPayeeFields() {
      if (!this.selectedMethod) return false;
      const name = this.selectedMethod.name;
      if (name === 'Gift card' || name === 'Customer credit') return false;
      if (name === 'Card') {
        if (this.gatewayId === 4) return false;
        if (this.gatewayId === 3 && this.selectedPaymentMethod !== 'new') return false;
        if (this.gatewayId === 2 && this.selectedPaymentMethod !== 'new' && this.squarePaymentMethods.length > 0) return false;
        return true;
      }
      return true;
    },
    payeeFirstNameLabel() {
      return this.selectedMethod && this.selectedMethod.name === 'Card' ? 'Cardholder first name' : 'Payee first name';
    },
    payeeLastNameLabel() {
      return this.selectedMethod && this.selectedMethod.name === 'Card' ? 'Cardholder last name' : 'Payee last name';
    }
  },

  watch: {
    booking() {
      // Reset form when booking data refreshes (e.g. after payment collected)
      this.paymentAmount = parseFloat(this.booking.due) || 0;
      this.successMessage = '';
      this.errorMessage = '';
      this.selectedGiftCard = null;
      this.giftCardCode = '';
      this.giftCardError = '';
      if (this.selectedMethod && this.selectedMethod.name === 'Customer credit' && this.booking.end_user_id) {
        this.loadCustomerCredit();
      }
    },
    async selectedMethod(method) {
      if (!method) return;
      this.selectedPaymentMethod = 'new';
      this.saveCard = false;
      if (method.name === 'Card') {
        await this.$nextTick();
        await this.initializePaymentForm();
        if (this.gatewayId === 3 && this.paymentSettings.store_credit_card && this.booking.end_user_id) {
          await this.loadStripePaymentMethods();
        }
        if (this.gatewayId === 2 && this.paymentSettings.square_store_credit_card && this.booking.end_user_id) {
          await this.loadSquarePaymentMethods();
        }
        if (this.gatewayId === 4) {
          await this.loadPaystack();
        }
      }
      if (method.name === 'Customer credit' && this.booking.end_user_id) {
        this.loadCustomerCredit();
      }
    },
    selectedPaymentMethod() {
      this.saveCard = false;
      if (this.selectedPaymentMethod === 'new' && this.gatewayId === 2) {
        this.reinitializeSquareForm();
      }
    }
  },

  async mounted() {
    this.paymentAmount = parseFloat(this.booking.due) || 0;
    this.cardholderFirstName = this.booking.customer_first_name || '';
    this.cardholderLastName = this.booking.customer_last_name || '';
    await this.loadPaymentOptions();
  },

  methods: {
    async getAuthToken() {
      const session = await Auth.currentSession();
      return session.getIdToken().getJwtToken();
    },

    formatMoney(val) {
      return parseFloat(val || 0).toFixed(2);
    },

    getAmount(value) {
      return formatCurrency(value, {
        currency: this.currency,
        currencySymbol: this.currencySymbol,
        currencyFormat: this.currencyFormat,
        numberFormat: this.numberFormat
      });
    },

    async loadPaymentOptions() {
      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/payment-methods`;
        const response = await axios.get(url, config);
        this.paymentOptions = response.data.payment_methods || [];

        if (this.paymentOptions.length > 0) {
          this.selectedMethod = this.paymentOptions[0];
        }
      } catch (error) {
        console.error('Failed to load payment options:', error);
      }
    },

    async loadStripePaymentMethods() {
      if (!this.booking.end_user_id) {
        this.stripePaymentMethods = [];
        this.stripeCustomer = null;
        return;
      }
      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/customers/${this.booking.end_user_id}/stripe-customer`;
        const response = await axios.get(url, config);
        this.stripeCustomer = response.data.stripe_customer;
        this.stripePaymentMethods = response.data.payment_methods || [];
        if (this.stripePaymentMethods.length > 0) {
          const defaultCard = this.stripePaymentMethods.find(pm => pm.is_default);
          if (defaultCard) this.selectedPaymentMethod = defaultCard.id;
        }
      } catch (err) {
        if (err.response?.status !== 404) {
          console.error('Error loading Stripe payment methods:', err);
        }
        this.stripePaymentMethods = [];
        this.stripeCustomer = null;
      }
    },

    async loadSquarePaymentMethods() {
      if (!this.booking.end_user_id) {
        this.squarePaymentMethods = [];
        this.squareCustomer = null;
        return;
      }
      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/customers/${this.booking.end_user_id}/square-customer`;
        const response = await axios.get(url, config);
        this.squareCustomer = response.data.square_customer || null;
        this.squarePaymentMethods = response.data.payment_methods || [];
        if (this.squarePaymentMethods.length > 0) {
          const defaultCard = this.squarePaymentMethods.find(pm => pm.is_default);
          this.selectedPaymentMethod = defaultCard ? defaultCard.id : this.squarePaymentMethods[0].id;
        } else {
          this.selectedPaymentMethod = 'new';
          await this.reinitializeSquareForm();
        }
      } catch (err) {
        if (err.response?.status !== 404) {
          console.error('Error loading Square payment methods:', err);
        }
        this.squarePaymentMethods = [];
        this.squareCustomer = null;
        this.selectedPaymentMethod = 'new';
        await this.reinitializeSquareForm();
      }
    },

    async reinitializeSquareForm() {
      if (this.gatewayId === 2) {
        if (this.squareCard) {
          try { await this.squareCard.destroy(); } catch (e) {}
          this.squareCard = null;
        }
        this.$nextTick(() => { this.initSquare(); });
      }
    },

    async loadCustomerCredit() {
      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/customers/${this.booking.end_user_id}/customer-credit`;
        const response = await axios.get(url, config);
        const credits = response.data.customer_credits || [];
        const total = credits.reduce((sum, credit) => sum + parseFloat(credit.remaining || 0), 0);
        this.availableCredit = this.formatMoney(total);
      } catch (error) {
        this.availableCredit = '0.00';
      }
    },

    async loadPaystack() {
      if (window.PaystackPop) return;
      return new Promise((resolve) => {
        const script = document.createElement('script');
        script.src = 'https://js.paystack.co/v2/inline.js';
        script.async = true;
        script.onload = resolve;
        document.head.appendChild(script);
      });
    },

    async giftCardSearch(queryString, cb) {
      if (!queryString || queryString.length < 2) {
        cb([]);
        return;
      }
      try {
        const token = await this.getAuthToken();
        const config = { headers: { Authorization: token } };
        const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/gifts/autocomplete?q=${encodeURIComponent(queryString)}`;
        const response = await axios.get(url, config);
        const suggestions = [];
        const giftCards = response.data.gift_cards || [];
        for (let n in giftCards) {
          if (giftCards[n].code != null) {
            suggestions.push({
              value: JSON.stringify(giftCards[n]),
              id: giftCards[n].id,
              code: giftCards[n].code,
              remaining: giftCards[n].remaining,
              total: giftCards[n].total
            });
          }
        }
        cb(suggestions);
      } catch (error) {
        cb([]);
      }
    },

    handleGiftCardSelect(item) {
      this.selectedGiftCard = item;
      this.giftCardCode = item.code;
      this.giftCardError = '';
    },


    getCardBrandName(brand) {
      const brands = { visa: 'Visa', mastercard: 'Mastercard', amex: 'Amex', discover: 'Discover', VISA: 'Visa', MASTERCARD: 'Mastercard' };
      return brands[brand] || brand || 'Card';
    },

    getCardBrandIcon(brand) {
      const icons = { visa: 'fab fa-cc-visa', mastercard: 'fab fa-cc-mastercard', amex: 'fab fa-cc-amex', discover: 'fab fa-cc-discover', VISA: 'fab fa-cc-visa', MASTERCARD: 'fab fa-cc-mastercard' };
      return icons[brand] || 'far fa-credit-card';
    },

    async loadStripe() {
      if (window.Stripe) return;
      return new Promise((resolve) => {
        const script = document.createElement('script');
        script.src = 'https://js.stripe.com/v3/';
        script.async = true;
        script.onload = resolve;
        document.body.appendChild(script);
      });
    },

    async loadSquare(isLive) {
      if (window.Square) return;
      return new Promise((resolve) => {
        const script = document.createElement('script');
        script.src = isLive
          ? 'https://web.squarecdn.com/v1/square.js'
          : 'https://sandbox.web.squarecdn.com/v1/square.js';
        script.async = true;
        script.onload = resolve;
        document.body.appendChild(script);
      });
    },

    async initializePaymentForm() {
      if (this.gatewayId === 3) {
        await this.loadStripe();
        await this.$nextTick();
        await this.initStripe();
      } else if (this.gatewayId === 2) {
        await this.loadSquare(!!this.paymentSettings.is_live);
        await this.$nextTick();
        await this.initSquare();
      }
    },

    async initStripe() {
      if (!window.Stripe || !this.paymentSettings.api_name) return;
      // Tear down any prior Element + empty the container so re-selecting
      // Card doesn't leave a stale iframe alongside the new one.
      if (this.stripeCard) {
        try { this.stripeCard.unmount(); } catch (e) {}
        try { this.stripeCard.destroy(); } catch (e) {}
        this.stripeCard = null;
      }
      const existingEl = document.getElementById('card-element-collect');
      if (existingEl) existingEl.innerHTML = '';
      // A Stripe Connect venue tokenizes inside its connected account (platform key + account).
      const accountOpts = this.paymentSettings.stripe_account_id ? { stripeAccount: this.paymentSettings.stripe_account_id } : undefined;
      this.stripe = window.Stripe(this.paymentSettings.api_name, accountOpts);
      const elements = this.stripe.elements();
      this.stripeCard = elements.create('card', {
        style: {
          base: {
            color: localStorage.getItem('mode') === '0' ? '#344675' : '#ffffff',
            fontFamily: 'Poppins, sans-serif',
            fontSize: '14px',
            '::placeholder': { color: '#6c757d' }
          }
        }
      });
      await this.$nextTick();
      const el = document.getElementById('card-element-collect');
      if (el) this.stripeCard.mount(el);
    },

    async initSquare() {
      if (!window.Square || !this.paymentSettings.api_name) return;
      // Destroy any prior card + empty the container so re-selecting Card
      // doesn't append a second iframe via Square's attach().
      if (this.squareCard) {
        try { await this.squareCard.destroy(); } catch (e) {}
        this.squareCard = null;
      }
      const existingEl = document.getElementById('sq-card-collect');
      if (existingEl) existingEl.innerHTML = '';
      this.square = window.Square.payments(
        this.paymentSettings.api_name,
        this.paymentSettings.api_location
      );
      try {
        const isDark = localStorage.getItem('mode') !== '0';
        const customStyle = {
          '.input-container': {
            borderRadius: '6px',
            borderColor: isDark ? 'rgba(255, 255, 255, 0.15)' : '#e3e3e3',
          },
          '.input-container.is-focus': {
            borderColor: '#e14eca',
          },
          '.input-container.is-error': {
            borderColor: '#ff1600',
          },
          '.message-text.is-error': {
            color: '#ff1600',
          },
          '.message-icon.is-error': {
            color: '#ff1600',
          },
          'input.is-error': {
            color: '#ff1600',
          },
          '.message-icon': {
            color: '#adb5bd',
          },
          '.message-text': {
            color: '#adb5bd',
          },
          input: {
            backgroundColor: isDark ? '#22233a' : '#ffffff',
            color: isDark ? '#ffffff' : '#344675',
            fontSize: '13px',
          },
          'input::placeholder': {
            color: '#adb5bd',
          },
        };
        this.squareCard = await this.square.card({ style: customStyle });
        await this.$nextTick();
        const el = document.getElementById('sq-card-collect');
        if (el) await this.squareCard.attach(el);
      } catch (e) {
        console.error('Initializing Square card failed', e);
      }
    },

    async submitPayment() {
      this.errorMessage = '';
      this.successMessage = '';
      this.processing = true;

      try {
        // Authorize.net: tokenize first
        if (this.gatewayId === 1 && this.selectedMethod.name === 'Card') {
          try {
            this.authorize_opaque_data = await this.$refs.authorizePaymentForm.tokenize();
            this.authorize_zip = this.$refs.authorizePaymentForm.zip;
          } catch (err) {
            throw new Error(err.message);
          }
        }

        if (this.selectedMethod.name === 'Card') {
          await this.handleCardPayment();
        } else if (this.selectedMethod.name === 'Gift card') {
          await this.handleGiftCardPayment();
        } else if (this.selectedMethod.name === 'Customer credit') {
          await this.handleCustomerCreditPayment();
        } else {
          await this.handleNonCardPayment();
        }
        this.$notify({ type: 'success', message: `${this.getAmount(this.effectiveAmount)} payment collected`, timeout: 3000 });
      } catch (error) {
        this.errorMessage = error.response?.data?.error || error.response?.data?.message || (typeof error.response?.data === 'string' ? error.response.data : null) || error.message || 'Payment failed';
      } finally {
        this.processing = false;
      }
    },

    async handleNonCardPayment() {
      let paymentType = 'cash';
      if (this.selectedMethod.name === 'POS payment') paymentType = 'pos';
      else if (this.selectedMethod.name === 'Yappy') paymentType = 'Yappy';
      else if (!this.selectedMethod.is_default) paymentType = this.selectedMethod.name;

      const token = await this.getAuthToken();
      const config = { headers: { Authorization: token } };
      const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/transactions/${this.booking.transaction_id}/payments`;

      const data = {
        amount: this.effectiveAmount,
        user: {
          first_name: this.cardholderFirstName || '',
          last_name: this.cardholderLastName || '',
          email: this.booking.customer_email || '',
          phone: this.booking.customer_phone || ''
        },
        payment_type: paymentType,
        is_default: this.selectedMethod.is_default
      };

      await axios.post(url, data, config);
      this.successMessage = `${this.getAmount(this.effectiveAmount)} collected`;
      this.$emit('payment-collected');
    },

    async handleGiftCardPayment() {
      if (!this.selectedGiftCard) throw new Error('Please select a gift card');

      const token = await this.getAuthToken();
      const config = { headers: { Authorization: token } };
      const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/transactions/${this.booking.transaction_id}/payments`;

      const data = {
        amount: this.effectiveAmount,
        gift_card: this.selectedGiftCard,
        payment_type: 'gift_card'
      };

      await axios.post(url, data, config);
      this.successMessage = `${this.getAmount(this.effectiveAmount)} collected`;
      this.$emit('payment-collected');
    },

    async handleCustomerCreditPayment() {
      const token = await this.getAuthToken();
      const config = { headers: { Authorization: token } };
      const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/transactions/${this.booking.transaction_id}/payments`;

      const data = {
        amount: this.effectiveAmount,
        user: {
          first_name: this.booking.customer_first_name || '',
          last_name: this.booking.customer_last_name || '',
          email: this.booking.customer_email || '',
          phone: this.booking.customer_phone || ''
        },
        customer_credit: this.effectiveAmount
      };

      await axios.post(url, data, config);
      this.successMessage = `${this.getAmount(this.effectiveAmount)} collected`;
      this.$emit('payment-collected');
    },

    async handleCardPayment() {
      if (this.gatewayId === 3) {
        await this.handleStripePayment();
      } else if (this.gatewayId === 2) {
        await this.handleSquarePayment();
      } else if (this.gatewayId === 1) {
        await this.handleAuthorizePayment();
      } else if (this.gatewayId === 4) {
        await this.handlePaystackPayment();
      }
    },

    async handleStripePayment() {
      if (!this.stripe || !this.stripeCard) throw new Error('Stripe not initialized');

      const authToken = await this.getAuthToken();
      const config = { headers: { Authorization: authToken } };
      const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/transactions/${this.booking.transaction_id}/payments/stripe`;

      const data = {
        amount: this.effectiveAmount,
        user: { first_name: this.cardholderFirstName, last_name: this.cardholderLastName },
        customer_id: this.booking.end_user_id
      };

      if (this.selectedPaymentMethod !== 'new' && this.selectedStripeStoredCard) {
        data.payment_method_id = this.selectedStripeStoredCard.payment_method_id;
        data.stripe_customer_id = this.stripeCustomer?.stripe_customer_id;
      } else {
        const { token, error } = await this.stripe.createToken(this.stripeCard);
        if (error) throw new Error(error.message);
        data.token = token.id;
        if (this.saveCard) {
          data.save_card = true;
        }
      }

      await axios.post(url, data, config);
      this.successMessage = `${this.getAmount(this.effectiveAmount)} collected`;
      this.$emit('payment-collected');
    },

    async tokenizeSquare(card) {
      const tokenResult = await card.tokenize();
      if (tokenResult.status === 'OK') {
        const zip = tokenResult.details?.billing?.postalCode || '';
        return { token: tokenResult.token, zip };
      } else {
        let errorMessage = `Tokenization failed with status: ${tokenResult.status}`;
        if (tokenResult.errors) {
          errorMessage += ` and errors: ${JSON.stringify(tokenResult.errors)}`;
        }
        throw new Error(errorMessage);
      }
    },

    async verifyBuyer(payments, token, zip) {
      const verificationDetails = {
        amount: this.effectiveAmount.toString(),
        billingContact: {
          familyName: this.cardholderLastName,
          givenName: this.cardholderFirstName,
          postalCode: zip
        },
        currencyCode: this.currency,
        intent: 'CHARGE',
      };
      const verificationResults = await payments.verifyBuyer(token, verificationDetails);
      return verificationResults.token;
    },

    async handleSquarePayment() {
      if (!this.squareCard) throw new Error('Square not initialized');

      const authToken = await this.getAuthToken();
      const config = { headers: { Authorization: authToken } };
      const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/transactions/${this.booking.transaction_id}/payments/square`;

      const data = {
        amount: this.effectiveAmount,
        user: { first_name: this.cardholderFirstName, last_name: this.cardholderLastName }
      };

      const isStoredCard = this.selectedPaymentMethod !== 'new' && this.selectedSquareStoredCard;

      if (isStoredCard) {
        data.square_card_id = this.selectedSquareStoredCard.card_id;
        data.square_customer_id = this.squareCustomer?.square_customer_id;
      } else {
        const tokenResult = await this.tokenizeSquare(this.squareCard);
        const verificationToken = await this.verifyBuyer(this.square, tokenResult.token, tokenResult.zip);

        data.sourceId = tokenResult.token;
        data.verificationToken = verificationToken;
        data.zip = tokenResult.zip;

        if (this.saveCard && this.booking.end_user_id) {
          const tokenResult2 = await this.tokenizeSquare(this.squareCard);
          data.sourceId2 = tokenResult2.token;
          data.save_card = true;
        }
      }

      await axios.post(url, data, config);
      this.successMessage = `${this.getAmount(this.effectiveAmount)} collected`;
      this.$emit('payment-collected');
    },

    async handleAuthorizePayment() {
      if (!this.authorize_opaque_data) throw new Error('Card tokenization failed');

      const authToken = await this.getAuthToken();
      const config = { headers: { Authorization: authToken } };
      const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/transactions/${this.booking.transaction_id}/payments`;

      const data = {
        amount: this.effectiveAmount,
        user: { first_name: this.cardholderFirstName, last_name: this.cardholderLastName },
        payment: { opaqueData: this.authorize_opaque_data, zip: this.authorize_zip }
      };

      await axios.post(url, data, config);
      this.successMessage = `${this.getAmount(this.effectiveAmount)} collected`;
      this.$emit('payment-collected');
    },

    async handlePaystackPayment() {
      // Metadata lets the paystack-webhook identify and record this charge
      // server-side if the callback below never reaches the lambda. A session
      // hiccup must never block a charge, so the operator email is best-effort.
      let operatorEmail = null;
      try {
        const session = await Auth.currentSession();
        operatorEmail = session.getIdToken().payload.email;
      } catch (e) {
        operatorEmail = null;
      }
      return new Promise((resolve, reject) => {
        const popup = new window.PaystackPop();
        popup.newTransaction({
          key: this.paymentSettings.api_name,
          email: this.booking.customer_email || '',
          amount: Math.round(Number(parseFloat(this.effectiveAmount) + 'e2')),
          reference: '' + Math.floor((Math.random() * 1000000000) + 1),
          metadata: {
            otc: 1,
            otc_flow: 'console_payment',
            otc_transaction_id: this.booking.transaction_id,
            otc_company_group_id: this.companyGroupId,
            otc_company_id: this.companyId,
            ...(operatorEmail ? { otc_user_email: operatorEmail } : {}),
            otc_first_name: this.cardholderFirstName,
            otc_last_name: this.cardholderLastName
          },
          onSuccess: (response) => {
            // The lambda reads paystack_reference.transaction (v1 field name);
            // v2 carries the numeric transaction id as response.trans (verified from a live callback).
            this.submitPaystackPayment({ ...response, transaction: response.trans })
              .then(() => {
                this.successMessage = `${this.getAmount(this.effectiveAmount)} collected`;
                this.$emit('payment-collected');
                resolve();
              })
              .catch(reject);
          },
          onCancel: () => {
            reject(new Error('Payment cancelled'));
          }
        });
      });
    },

    async submitPaystackPayment(reference) {
      const authToken = await this.getAuthToken();
      const config = { headers: { Authorization: authToken } };
      const url = `${API_LOCATION}companies/${this.companyId}/groups/${this.companyGroupId}/transactions/${this.booking.transaction_id}/payments`;

      const data = {
        amount: this.effectiveAmount,
        user: { first_name: this.cardholderFirstName, last_name: this.cardholderLastName },
        paystack_reference: reference
      };

      await axios.post(url, data, config);
    }
  }
};
</script>

<style scoped lang="scss">
.collect-payment-view {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.field-label {
  display: block;
  font-size: 12px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 6px;
}

.view-header {
  display: flex;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  gap: 12px;
}

.back-btn {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  padding: 4px;
  font-size: 14px;
  &:hover { color: #fff; }
}

.view-title {
  color: #fff;
  font-size: 15px;
  font-weight: 600;
}

.view-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}

.collect-payment-summary {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 20px;
}

label {
  font-size: 13px;
}

.amount-input-group {
  display: flex;
  align-items: stretch;
  border: 1px solid rgba(225, 78, 202, 0.4);
  border-radius: 0.4285rem;
  overflow: hidden;
  background: rgba(30, 30, 47, 0.6);

  &:focus-within {
    border-color: #e14eca;
  }

  &.has-error {
    border-color: #ec250d;
  }

  &.disabled {
    opacity: 0.6;
    cursor: not-allowed;

    .amount-prefix {
      background: rgba(225, 78, 202, 0.08);
    }
  }
}

.amount-prefix {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 0.875rem;
  background: rgba(225, 78, 202, 0.15);
  font-weight: 600;
  font-size: 0.875rem;
  min-width: 40px;
  color: #e14eca;
}

.amount-input-wrapper {
  flex: 1;

  ::v-deep .form-control {
    border: none;
    border-radius: 0;
    background: transparent;
  }

  ::v-deep .el-input-number {
    width: 100%;
  }

  ::v-deep .el-input-number .el-input__inner {
    text-align: left;
    color: white;
    background: transparent;
    border: none;
  }
}

.stripe-card-element {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  padding: 12px;
}

.stored-card-display {
  background: linear-gradient(135deg, #171826 0%, #2d2d44 100%);
  border: 2px solid rgba(225, 78, 202, 0.25);
  border-radius: 8px;
  padding: 12px 16px;
}

.paystack-info {
  background: rgba(45, 206, 137, 0.08);
  border: 1px solid rgba(45, 206, 137, 0.2);
  border-radius: 6px;
  padding: 10px 12px;
}

.payment-error {
  color: #F5365C;
  font-size: 13px;
  padding: 10px 12px;
  background: rgba(245, 54, 92, 0.1);
  border-radius: 6px;
}

.payment-success {
  color: #2DCE89;
  font-size: 13px;
  padding: 10px 12px;
  background: rgba(45, 206, 137, 0.1);
  border-radius: 6px;
}

.view-footer {
  display: flex;
  justify-content: flex-end;
  padding: 14px 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  gap: 8px;
}

// Light mode
.white-content {
  .field-label {
    color: #525f7f;
  }
  .view-header {
    border-color: #e3e3e3;
  }
  .view-title {
    color: #1E1E2F;
  }
  .back-btn {
    color: rgba(0, 0, 0, 0.5);
    &:hover { color: #1E1E2F; }
  }
  .collect-payment-summary {
    background: #f8f9fa;
    border-color: #e3e3e3;
  }
  .stripe-card-element {
    background: #fff;
    border-color: #e3e3e3;
  }
  .stored-card-display {
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    border-color: rgba(225, 78, 202, 0.3);
    p, small { color: #1E1E2F !important; }
  }
  .amount-input-group {
    background: #ffffff;
  }

  .amount-input-wrapper ::v-deep .el-input-number .el-input__inner {
    color: #344675;
  }

  .view-footer {
    border-color: #e3e3e3;
  }

  .collect-payment-view .text-white {
    color: #344675 !important;
  }
}
</style>
